import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// Shared base styles applied to every button.
const buttonBase = [
  "inline-flex items-center justify-center gap-2 whitespace-nowrap",
  "rounded-md text-sm font-medium shrink-0 outline-none select-none",
  "transition-all duration-[0.12s] ease-[cubic-bezier(0.4,0,0.2,1)]",
  "active:translate-y-px active:shadow-[var(--shadow-pressed)]",
  "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
  "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  "disabled:pointer-events-none disabled:opacity-50 disabled:active:translate-y-0",
  "aria-busy:pointer-events-none",
  "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
].join(" ")

const buttonVariants = cva(buttonBase, {
  variants: {
    variant: {
      // Canonical emphasis order: primary (default) → secondary → outline.
      // `default` is the primary emphasis button (brand-green, raised).
      default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-[var(--raised-dark)] dark:bg-primary/75 dark:hover:bg-primary/85",
      // Secondary: clean white with brand-green text — medium emphasis.
      secondary:
        "bg-white text-primary shadow-[0_0_0_1px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,1),inset_0_-2px_0_rgba(0,0,0,0.10)] hover:bg-[#fcfcfc] hover:shadow-[0_0_0_1px_rgba(0,0,0,0.12),inset_0_1px_0_rgba(255,255,255,1),inset_0_-2px_0_rgba(0,0,0,0.14)] focus-visible:ring-ring/30 dark:bg-white/5 dark:text-white dark:shadow-[0_0_0_1px_rgba(255,255,255,0.08),inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-2px_0_rgba(0,0,0,0.70)] dark:hover:bg-white/10 dark:focus-visible:border-transparent dark:focus-visible:ring-0 dark:focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.28),inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-2px_0_rgba(0,0,0,0.70)]",
      // Outline: tertiary emphasis — 1px border on transparent, no raised
      // bevel. Clearly lower-weight than secondary so the two read as
      // different levels in an action row.
      outline:
        "border border-border bg-transparent text-foreground hover:bg-muted/60 focus-visible:ring-ring/30 dark:border-white/10 dark:text-foreground dark:hover:bg-white/[0.04] dark:focus-visible:border-transparent dark:focus-visible:ring-0 dark:focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.22)]",
      destructive:
        "bg-destructive text-destructive-foreground shadow-[var(--raised-dark)] hover:brightness-105",
      ghost: "text-foreground hover:bg-secondary/60 dark:bg-secondary/40 dark:hover:bg-secondary/60",
      link: "text-primary underline-offset-4 hover:underline",
      soft: "text-primary bg-primary/10 hover:bg-primary/15 dark:bg-primary/20 dark:hover:bg-primary/30",
      // New skill variants — require tone= to compute the color compound.
      raised: "border border-transparent",
      flat: "border border-transparent bg-transparent",
    },
    tone: {
      primary: "",
      secondary: "",
      info: "",
      success: "",
      warning: "",
      danger: "",
    },
    size: {
      default: "h-9 px-4 py-2 has-[>svg]:px-3",
      sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
      md: "h-10 px-3.5 text-sm rounded-[8px]",
      lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
      icon: "size-9",
      "icon-sm": "size-8",
      "icon-lg": "size-10",
    },
  },
  compoundVariants: [
    // DEFAULT × tone (default now has raised bevel; tone refines color + shadow)
    { variant: "default", tone: "primary", class: "bg-primary text-primary-foreground shadow-[var(--raised-dark)] hover:brightness-105 dark:bg-primary/75 dark:hover:bg-primary/85 dark:hover:brightness-100" },
    { variant: "default", tone: "secondary", class: "bg-white text-primary shadow-[0_0_0_1px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,1),inset_0_-2px_0_rgba(0,0,0,0.10)] hover:bg-[#fcfcfc] hover:shadow-[0_0_0_1px_rgba(0,0,0,0.12),inset_0_1px_0_rgba(255,255,255,1),inset_0_-2px_0_rgba(0,0,0,0.14)] focus-visible:ring-ring/30 dark:bg-white/5 dark:text-white dark:shadow-[0_0_0_1px_rgba(255,255,255,0.08),inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-2px_0_rgba(0,0,0,0.70)] dark:hover:bg-white/10 dark:focus-visible:border-transparent dark:focus-visible:ring-0 dark:focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.28),inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-2px_0_rgba(0,0,0,0.70)]" },
    { variant: "default", tone: "info", class: "bg-info text-info-foreground shadow-[var(--raised-dark)] hover:brightness-105" },
    { variant: "default", tone: "success", class: "bg-success text-success-foreground shadow-[var(--raised-dark)] hover:brightness-105" },
    { variant: "default", tone: "warning", class: "bg-warning text-warning-foreground shadow-[var(--raised-dark)] hover:brightness-105" },
    { variant: "default", tone: "danger", class: "bg-destructive text-destructive-foreground shadow-[var(--raised-dark)] hover:brightness-105" },

    // RAISED × tone
    { variant: "raised", tone: "primary", class: "bg-primary text-primary-foreground shadow-[var(--raised-dark)] hover:brightness-105 dark:bg-primary/75 dark:hover:bg-primary/85 dark:hover:brightness-100" },
    { variant: "raised", tone: "secondary", class: "bg-white text-primary shadow-[0_0_0_1px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,1),inset_0_-2px_0_rgba(0,0,0,0.10)] hover:bg-[#fcfcfc] hover:shadow-[0_0_0_1px_rgba(0,0,0,0.12),inset_0_1px_0_rgba(255,255,255,1),inset_0_-2px_0_rgba(0,0,0,0.14)] focus-visible:ring-ring/30 dark:bg-white/5 dark:text-white dark:shadow-[0_0_0_1px_rgba(255,255,255,0.08),inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-2px_0_rgba(0,0,0,0.70)] dark:hover:bg-white/10 dark:focus-visible:border-transparent dark:focus-visible:ring-0 dark:focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.28),inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-2px_0_rgba(0,0,0,0.70)]" },
    { variant: "raised", tone: "info", class: "bg-info text-info-foreground shadow-[var(--raised-dark)] hover:brightness-105" },
    { variant: "raised", tone: "success", class: "bg-success text-success-foreground shadow-[var(--raised-dark)] hover:brightness-105" },
    { variant: "raised", tone: "warning", class: "bg-warning text-warning-foreground shadow-[var(--raised-dark)] hover:brightness-105" },
    { variant: "raised", tone: "danger", class: "bg-destructive text-destructive-foreground shadow-[var(--raised-dark)] hover:brightness-105" },

    // FLAT × tone
    { variant: "flat", tone: "primary", class: "text-primary bg-primary/10 hover:bg-primary/15 dark:bg-primary/20 dark:hover:bg-primary/30" },
    { variant: "flat", tone: "secondary", class: "text-foreground hover:bg-secondary/60 dark:bg-secondary/50 dark:hover:bg-secondary/70" },
    { variant: "flat", tone: "info", class: "text-info bg-info/10 hover:bg-info/15 dark:bg-info/20 dark:hover:bg-info/30" },
    { variant: "flat", tone: "success", class: "text-success bg-success/10 hover:bg-success/15 dark:bg-success/20 dark:hover:bg-success/30" },
    { variant: "flat", tone: "warning", class: "text-warning bg-warning/10 hover:bg-warning/15 dark:bg-warning/20 dark:hover:bg-warning/30" },
    { variant: "flat", tone: "danger", class: "text-destructive bg-destructive/10 hover:bg-destructive/15 dark:bg-destructive/20 dark:hover:bg-destructive/30" },

    // OUTLINE × tone (decorates legacy outline variant when tone is set)
    { variant: "outline", tone: "primary", class: "border border-primary text-primary bg-background shadow-[var(--raised-light)] hover:bg-primary/5" },
    { variant: "outline", tone: "secondary", class: "bg-white text-primary shadow-[0_0_0_1px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,1),inset_0_-2px_0_rgba(0,0,0,0.10)] hover:bg-[#fcfcfc] hover:shadow-[0_0_0_1px_rgba(0,0,0,0.12),inset_0_1px_0_rgba(255,255,255,1),inset_0_-2px_0_rgba(0,0,0,0.14)] focus-visible:ring-ring/30 dark:bg-white/5 dark:text-white dark:shadow-[0_0_0_1px_rgba(255,255,255,0.08),inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-2px_0_rgba(0,0,0,0.70)] dark:hover:bg-white/10 dark:focus-visible:border-transparent dark:focus-visible:ring-0 dark:focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.28),inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-2px_0_rgba(0,0,0,0.70)]" },
    { variant: "outline", tone: "info", class: "border border-info text-info bg-background shadow-[var(--raised-light)] hover:bg-info/5" },
    { variant: "outline", tone: "success", class: "border border-success text-success bg-background shadow-[var(--raised-light)] hover:bg-success/5" },
    { variant: "outline", tone: "warning", class: "border border-warning text-warning bg-background shadow-[var(--raised-light)] hover:bg-warning/5" },
    { variant: "outline", tone: "danger", class: "border border-destructive text-destructive bg-background shadow-[var(--raised-light)] hover:bg-destructive/5" },
  ],
  defaultVariants: {
    variant: "default",
    size: "default",
  },
})

export interface ButtonProps
  extends React.ComponentProps<"button">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  render?: (props: any) => React.ReactNode
  loading?: boolean
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button({
  className,
  variant,
  tone,
  size,
  asChild = false,
  render,
  loading = false,
  disabled,
  iconLeft,
  iconRight,
  children,
  ...props
}, ref) {
  const buttonClasses = cn(buttonVariants({ variant, tone, size }), className)

  const content = (
    <>
      {iconLeft ? <span className="inline-flex h-4 w-4 shrink-0">{iconLeft}</span> : null}
      {loading ? (
        <>
          <span aria-hidden className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
          {children}
        </>
      ) : (
        children
      )}
      {iconRight ? <span className="inline-flex h-4 w-4 shrink-0">{iconRight}</span> : null}
    </>
  )

  const buttonProps: any = {
    "data-slot": "button",
    className: buttonClasses,
    "aria-busy": loading || undefined,
    disabled: disabled || loading,
    ...props,
  }

  // New render prop API
  if (render) {
    return render({ ...buttonProps, children: content })
  }

  // Legacy asChild API - merge props onto child element
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, {
      ...buttonProps,
      children: (children as React.ReactElement<any>).props.children,
    })
  }

  return <button ref={ref} {...buttonProps}>{content}</button>
})

export { Button, buttonVariants }
